import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { createClient } from "@/lib/connect";
import { TrafficSourceService } from "@gen/tracker/v1/traffic_source_connect";
import {
  TrafficSource,
  TrafficSourceKind,
  TrafficSourceStatus,
} from "@gen/tracker/v1/traffic_source_pb";
import { ListSort, Pagination } from "@gen/tracker/v1/common_pb";
import { fetchAll, useByIds } from "@/lib/services/catalog";

const client = createClient(TrafficSourceService);

export const trafficSourceKeys = {
  all: ["traffic_sources"] as const,
  list: (params: ListTrafficSourcesParams) => [...trafficSourceKeys.all, "list", params] as const,
  detail: (id: string) => [...trafficSourceKeys.all, "detail", id] as const,
};

export interface ListTrafficSourcesParams {
  search?: string;
  kinds?: TrafficSourceKind[];
  statuses?: TrafficSourceStatus[];
  ids?: string[];
  /** name | kind | created_at; none = by name. */
  sort?: ListSort;
  /** One page; the API's default is 50 and its ceiling 500. */
  limit?: number;
  offset?: number;
}

export function useListTrafficSources(
  params: ListTrafficSourcesParams = {},
  options?: Partial<UseQueryOptions<{ items: TrafficSource[]; total: number }>>,
) {
  return useQuery({
    queryKey: trafficSourceKeys.list(params),
    queryFn: async () => {
      const res = await client.listTrafficSources({
        search: params.search ?? "",
        kinds: params.kinds ?? [],
        statuses: params.statuses ?? [],
        tagIds: [],
        ids: params.ids ?? [],
        sort: params.sort,
        pagination: new Pagination({
          limit: params.limit ?? 50,
          offset: params.offset ?? 0,
        }),
      });
      return { items: res.items, total: res.pagination?.total ?? res.items.length };
    },
    ...options,
  });
}

/** Every traffic source of the workspace (there are a handful), page after page. */
export function useAllTrafficSources(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...trafficSourceKeys.all, "all"] as const,
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
    queryFn: () =>
      fetchAll(async (offset, limit) => {
        const res = await client.listTrafficSources({ pagination: new Pagination({ limit, offset }) });
        return { items: res.items, total: res.pagination?.total ?? res.items.length };
      }),
  });
}

/** The sources with these ids, by id — archived ones too. */
export function useTrafficSourcesByIds(ids: Iterable<string>, enabled = true) {
  return useByIds<TrafficSource>(
    trafficSourceKeys.all,
    ids,
    async (chunk) => (await client.listTrafficSources({ ids: chunk, pagination: new Pagination({ limit: chunk.length, offset: 0 }) })).items,
    enabled,
  );
}

export function useGetTrafficSource(id: string | undefined) {
  return useQuery({
    queryKey: trafficSourceKeys.detail(id ?? ""),
    enabled: !!id,
    queryFn: async () => {
      if (!id) return undefined;
      const res = await client.getTrafficSource({ id });
      return res.trafficSource;
    },
  });
}

export function useCreateTrafficSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ts: TrafficSource) => {
      const res = await client.createTrafficSource({ trafficSource: ts });
      return res.trafficSource!;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: trafficSourceKeys.all });
    },
  });
}

export function useUpdateTrafficSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ts: TrafficSource) => {
      const res = await client.updateTrafficSource({ trafficSource: ts });
      return res.trafficSource!;
    },
    onSuccess: (ts) => {
      void qc.invalidateQueries({ queryKey: trafficSourceKeys.all });
      qc.setQueryData(trafficSourceKeys.detail(ts.id), ts);
    },
  });
}

export function useArchiveTrafficSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await client.archiveTrafficSource({ id });
      return id;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: trafficSourceKeys.all });
    },
  });
}

export { trafficSourceKindLabel } from "@/lib/source-types";
